import { errorMessages } from '~/constants/errorMessages.ts';
import {
  addressNotRequiredSchema,
  addressSchema,
  identificationNumberSchema,
  notRequiredOptionSchema,
  requiredOptionSchema,
} from '../../validationSchemas';
import * as Yup from 'yup';
import { optionToEntityMap } from './constants';
import { latinAndUkrCharactersOnly } from '~/helpers/validation';
import { TRelatedCompanies, TRelatedEntity } from './types';
import { getValidationFormValue } from '~/helpers/utils';

export const validationSchema = Yup.object().shape({
  isSeparateUnitsAbsent: Yup.boolean().nullable(),
  separateUnits: Yup.array()
    .nullable()
    .of(
      Yup.object().shape({
        fullName: Yup.string()
          .nullable()
          .when('isSeparateUnitsAbsent', function ([], schema, options) {
            return getValidationFormValue<TRelatedCompanies>(options, 1)?.isSeparateUnitsAbsent
              ? schema.notRequired()
              : schema
                  .transform((_, orin) => (orin?.trim() === '' ? null : orin))
                  .concat(latinAndUkrCharactersOnly)
                  .required(errorMessages.FIELD_REQUIRED);
          }),
        identificationNumber: Yup.string()
          .test('is-numeric', function (value) {
            if (!(!value || /^\d+$/.test(value))) {
              return this.createError({
                path: this.path,
                message: errorMessages.MUST_BE_NUMBER,
              });
            }
            return true;
          })
          .nullable()
          .notRequired(),
        residenceCountry: Yup.object().when('isSeparateUnitsAbsent', function ([], _, options) {
          return getValidationFormValue<TRelatedCompanies>(options, 1)?.isSeparateUnitsAbsent
            ? notRequiredOptionSchema
            : requiredOptionSchema;
        }),
        unitRegistrationDate: Yup.string()
          .nullable()
          .when('isSeparateUnitsAbsent', function ([], schema, options) {
            return getValidationFormValue<TRelatedCompanies>(options, 1)?.isSeparateUnitsAbsent
              ? schema.nullable().notRequired()
              : schema.required(errorMessages.FIELD_REQUIRED);
          }),
        address: Yup.object().shape({
          registrationAddress: Yup.object().when(
            'isSeparateUnitsAbsent',
            function ([], _, options) {
              return getValidationFormValue<TRelatedCompanies>(options, 2)?.isSeparateUnitsAbsent
                ? addressNotRequiredSchema
                : addressSchema;
            }
          ),
          permanentAddress: Yup.object().when('isSeparateUnitsAbsent', function ([], _, options) {
            return getValidationFormValue<TRelatedCompanies>(options, 2)?.isSeparateUnitsAbsent
              ? addressNotRequiredSchema
              : addressSchema;
          }),
          coincidences: Yup.boolean().nullable(),
        }),
        separateUnitType: Yup.object().when('isSeparateUnitsAbsent', function ([], _, options) {
          return getValidationFormValue<TRelatedCompanies>(options, 1)?.isSeparateUnitsAbsent
            ? notRequiredOptionSchema
            : requiredOptionSchema;
        }),
      })
    ),
  relatedEntityInfo: Yup.object().shape({
    entity: Yup.string().required(errorMessages.RADIO_OPTION_REQUIRED),
    fullName: Yup.string()
      .nullable()
      .when('entity', function ([entity], schema) {
        return entity === optionToEntityMap.option0
          ? schema.notRequired()
          : schema
              .transform((_, orin) => (orin?.trim() === '' ? null : orin))
              .concat(latinAndUkrCharactersOnly)
              .required(errorMessages.FIELD_REQUIRED);
      }),
    identificationNumber: Yup.string().when('entity', function ([entity], schema) {
      return entity === optionToEntityMap.option0
        ? schema.notRequired()
        : identificationNumberSchema().required(errorMessages.FIELD_REQUIRED);
    }),
    residenceCountry: Yup.object().when('entity', function ([entity]) {
      return entity === optionToEntityMap.option0 ? notRequiredOptionSchema : requiredOptionSchema;
    }),
    address: Yup.object()
      .nullable()
      .shape({
        registrationAddress: Yup.object().when('coincidences', function ([], _, options) {
          return getValidationFormValue<TRelatedEntity>(options, 1)?.entity ===
            optionToEntityMap.option0
            ? addressNotRequiredSchema
            : addressSchema;
        }),
        permanentAddress: Yup.object().when('coincidences', function ([], _, options) {
          return getValidationFormValue<TRelatedEntity>(options, 1)?.entity ===
            optionToEntityMap.option0
            ? addressNotRequiredSchema
            : addressSchema;
        }),
        coincidences: Yup.boolean().nullable(),
      }),
  }),
});
